'use client';

import { useCallback } from 'react';

import type { Json } from '@kit/supabase/database';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import {
  usePersonalAccountData,
  useRevalidatePersonalAccountDataQuery,
} from './use-personal-account-data';

/**
 * Record the reader's chosen language on their own account.
 *
 * The cookie is what actually changes the language, and the caller sets it. This
 * is the durable half: without it the preference belongs to a browser rather
 * than a person, and signing in from a laptop after choosing Arabic on a phone
 * lands back in English with no explanation.
 *
 * ## Why there is no server action
 *
 * The row is the caller's own personal account. `accounts` already grants
 * `authenticated` a column-scoped UPDATE that includes `public_data`, under a
 * policy of `auth.uid() = primary_owner_user_id` — which on a personal account
 * is the user themselves. Postgres is already enforcing exactly the rule this
 * feature needs: you may set your own language and nobody else's, an account
 * owner and a super admin included. A server action could only re-state a
 * weaker version of that check.
 */
export function usePersistLocale(userId: string) {
  const client = useSupabase();
  const revalidate = useRevalidatePersonalAccountDataQuery();
  const { data } = usePersonalAccountData(userId);

  return useCallback(
    (locale: string) => {
      /*
       * Nothing to merge into yet.
       *
       * `public_data` is a shared bag, so writing `{ locale }` without the
       * current contents would drop every other key in it. Skipping is the
       * safe failure: the cookie has already switched the language, and the
       * record catches up the next time it is set. Clobbering someone's other
       * preferences to save a round trip is not a trade worth making.
       */
      if (!data) {
        return Promise.resolve();
      }

      const existing =
        typeof data.public_data === 'object' && data.public_data !== null
          ? (data.public_data as Record<string, Json>)
          : {};

      /*
       * Returned so the caller can await it.
       *
       * Switching language now triggers a full page load, and an in-flight
       * fetch does not survive one — fire-and-forget meant the cookie changed
       * and the record silently did not, so the choice was forgotten on the
       * next device.
       */
      return client
        .from('accounts')
        .update({ public_data: { ...existing, locale } })
        .eq('id', data.id)
        .then(() => revalidate(userId));
    },
    [client, data, revalidate, userId],
  );
}
